import { useEffect, useRef, useState } from "react";
import {
  ArrowUpRight,
  BookOpen,
  CircleHelp,
  MessageSquare,
  Play,
  Zap,
} from "lucide-react";
import { Button } from "@hub/ui";
import { HELP_LINKS } from "./tour";
import { useAuditTour } from "./TourProvider";

// The "?" control and its Help & guidance menu. Ported from the prototype's
// `HelpMenu`, which is the only entry point to the walkthrough.
//
// Menu-triggered ONLY, deliberately matching the prototype: the tour does not
// auto-run on first visit. A tour that starts itself over a reviewer's queue is
// an interruption, and this one crosses screens — it would navigate someone out
// of a case they had already started reading.

const LINK_ICONS = [BookOpen, CircleHelp, Zap, MessageSquare];

export function HelpMenu() {
  const tour = useAuditTour();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on Escape and on a click outside. Both, not either: a menu that traps
  // the pointer is worse than one that closes too eagerly.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node))
        setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  // No tour provider mounted (tests, or a surface outside the audit workbench):
  // render nothing rather than a Help button whose primary item does nothing.
  if (!tour) return null;

  return (
    <div ref={rootRef} className="relative">
      <Button
        variant="ghost"
        size="icon"
        data-tour="tour-help"
        title="Help & guided tour"
        aria-label="Help and guided tour"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        <CircleHelp className="size-4" />
      </Button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+8px)] z-[150] w-72 rounded-xl border bg-background p-2 shadow-2xl"
        >
          <div className="px-2.5 pb-1.5 pt-2 text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
            Help &amp; guidance
          </div>

          {/* The walkthrough sits apart from the links: it is the only item that
              acts on THIS app rather than opening something elsewhere, and the
              prototype gives it the filled treatment to say so. */}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              tour.start();
            }}
            className="mb-1 flex w-full items-center gap-3 rounded-lg p-2.5 text-left transition-colors"
            style={{
              background:
                "color-mix(in srgb, var(--ch-blue-500) 10%, transparent)",
            }}
          >
            <span
              className="inline-flex size-[30px] shrink-0 items-center justify-center rounded-lg"
              style={{
                background:
                  "color-mix(in srgb, var(--ch-blue-500) 20%, transparent)",
              }}
            >
              <Play
                className="size-3.5"
                style={{ color: "var(--ch-blue-700)" }}
              />
            </span>
            <span className="flex flex-col gap-px">
              <span className="text-[13px] font-semibold">
                Start the walkthrough
              </span>
              <span className="text-[11px] text-muted-foreground">
                Replay the guided tour for this screen
              </span>
            </span>
          </button>

          {HELP_LINKS.map((link, i) => {
            const Icon = LINK_ICONS[i] ?? CircleHelp;
            const isMail = link.href.startsWith("mailto");
            return (
              <a
                key={link.label}
                role="menuitem"
                href={link.href}
                target={isMail ? undefined : "_blank"}
                rel="noreferrer"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-lg p-2.5 text-inherit no-underline transition-colors hover:bg-muted"
              >
                <span className="inline-flex size-[30px] shrink-0 items-center justify-center rounded-lg bg-muted">
                  <Icon className="size-3.5 text-muted-foreground" />
                </span>
                <span className="flex flex-col gap-px">
                  <span className="text-[13px] font-semibold">
                    {link.label}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {link.desc}
                  </span>
                </span>
                <span className="flex-1" />
                <ArrowUpRight className="size-3 shrink-0 text-muted-foreground" />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
